import { DownOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Checkbox, Dropdown } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import React from 'react';
import { CheckboxMenuOptions, SelectOptions } from '../../../model/CommonModel';
import styles from './checkboxMenu.module.scss';

type Props<T> = {
	checked: boolean;
	customMenuOptions?: SelectOptions<T>[];
	disabled?: boolean;
	id?: string;
	indeterminate?: boolean;
	ariaLabel?: string;
	onChange: (value: boolean) => void;
	onClickOption?: (value: T) => void;
};

const CheckboxMenu = <T extends object | string | number>({
	customMenuOptions,
	disabled,
	id,
	checked,
	indeterminate,
	ariaLabel,
	onChange,
	onClickOption,
}: Props<T>) => {
	const defaultMenuOptions: SelectOptions<CheckboxMenuOptions>[] = [
		{
			id: 'selectAllOption',
			label: _('SelectAllData'),
			value: CheckboxMenuOptions.SELECT_ALL,
		},
		{
			id: 'clearAllOption',
			label: _('ClearAllData'),
			value: CheckboxMenuOptions.CLEAR_ALL,
		},
	];

	const menuOptions = customMenuOptions ? customMenuOptions : defaultMenuOptions;

	const items: MenuProps['items'] = menuOptions.map(({ label, value, icon, separator, disabled, title, id }, index) => ({
		id,
		key: `${label}-${index}`,
		icon,
		onClick: () => onClickOption?.(value),
		className: separator ? styles.separator : undefined,
		disabled,
		title,
		label,
	}));

	const handleOnChange = (e: CheckboxChangeEvent) => {
		const { checked } = e.target;
		onChange(checked);
	};

	return (
		<div className={styles.checkboxMenu}>
			<Checkbox id={id} checked={checked} onChange={handleOnChange} indeterminate={indeterminate} aria-label={ariaLabel}>
				<label htmlFor={id} className={styles.srOnly}>{`${_('SelectItem')}`}</label>
			</Checkbox>
			<Dropdown menu={{ items }} disabled={disabled} overlayStyle={{ width: 'max-content' }}>
				<DownOutlined className={styles.checkboxMenuIcon} />
			</Dropdown>
		</div>
	);
};

export { CheckboxMenu };
